import React, {useCallback} from 'react';

import {BulkActionDownload, BulkActionResultModal} from 'src/components/bulk-tool';
import {CompleteMark} from 'src/components/complete-mark';
import {UserLink} from 'src/components/links';
import {RowNumbered} from 'src/components/row-numbered';
import {useNewsletterDictionary} from 'src/hooks/dictionaries/useNewsletterDictionary';
import {getNewsletterBulkUpdateResult, getNewsletterUpdateProgress} from 'src/openapi/admin/apis/UserNewsletterBulkUpdateApi';
import {useAppDispatch} from 'src/store/hooks';

import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';

import {FILE_HEADERS, FILE_NAME, TOOL_TITLE} from './constants';

import type {NewsletterProcessedItem} from 'src/openapi/admin/models/NewsletterProcessedItem';
import type {Pagination} from 'src/openapi/admin/models/Pagination';


type Props = {
    id: string,
    onClose: () => void,
};

export const UpdateUserNewsletterResultModal = ({id, onClose}: Props) => {
    const apiDispatch = useAppDispatch();
    const {newsletterLabels} = useNewsletterDictionary();

    const getPaginatedResults = useCallback(
        (bulkResult: NewsletterProcessedItem[], {currentPage, pageSize}: Pagination) => bulkResult
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map((res: NewsletterProcessedItem, index) => (
                <Row key={index}>
                    <Cell width={50}>
                        <RowNumbered
                            maxRowNumber={bulkResult?.length || 0}
                            placeholder={(currentPage - 1) * pageSize + index + 1}
                        />
                    </Cell>
                    <Cell>
                        <UserLink
                            userId={res.userId || 0}
                            caption={res.email}
                            blank={true}
                            place={`Bulk ${TOOL_TITLE} Result`}
                        />
                    </Cell>
                    <Cell>
                        <EllipsisTextHint title={res.oldTypes.map(type => newsletterLabels[type]).join('; ')}>
                            {res.oldTypes.map(type => newsletterLabels[type]).join('; ') || 'No Newsletter'}
                        </EllipsisTextHint>
                    </Cell>
                    <Cell>
                        <EllipsisTextHint title={res.newTypes.map(type => newsletterLabels[type]).join('; ')}>
                            {res.newTypes.map(type => newsletterLabels[type]).join('; ') || 'No Newsletter'}
                        </EllipsisTextHint>
                    </Cell>
                    <Cell>
                        <CompleteMark errors={res.errors}/>
                    </Cell>
                </Row>
            )),
        [newsletterLabels],
    );

    return (
        <BulkActionResultModal<NewsletterProcessedItem>
            id={id}
            onClose={onClose}

            toolTitle={TOOL_TITLE}
            description={bulkResult => (
                <Group justify="space-between" alignItems="center">
                    <b>Update results</b>
                    <BulkActionDownload
                        toolName={TOOL_TITLE}
                        fileName={FILE_NAME}
                        headers={FILE_HEADERS}
                        startTime={bulkResult.startTime}
                        body={bulkResult.itemList?.map(item => ({
                            ...item,
                            userId: item.userId?.toString(),
                            oldTypes: item.oldTypes.map(type => newsletterLabels[type]).join('; ') || 'No Newsletter',
                            newTypes: item.newTypes.map(type => newsletterLabels[type]).join('; ') || 'No Newsletter',
                            errors: item.errors?.join('. '),
                        })) as Record<string, string>[]}
                    />
                </Group>
            )}
            table={(bulkResult, pagination) => (
                <Table borderedGrid={true}>
                    <Head>
                        <Cell width={50}><strong>#</strong></Cell>
                        <Cell width={246}>User Email</Cell>
                        <Cell width={200}>Old Newsletters</Cell>
                        <Cell width={200}>New Newsletters</Cell>
                        <Cell width={150}>Action Status</Cell>
                    </Head>
                    <Body>
                        {getPaginatedResults(bulkResult, pagination)}
                    </Body>
                </Table>
            )}

            getProgress={() => apiDispatch(getNewsletterUpdateProgress({id}))}
            getResult={() => apiDispatch(getNewsletterBulkUpdateResult({id}))}

            sortComparator={(a, b) => a.email.localeCompare(b.email)}
        />
    );
};
